import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState, type CSSProperties } from 'react';

const NAV_ITEMS = [
  { to: '/services', label: '売買・賃貸仲介' },
  { to: '/renovation', label: '買取・再販' },
  { to: '/inheritance', label: '相続相談' },
  { to: '/company', label: '会社概要' },
];

// 写真の上にヘッダーを重ねるページ
const OVERLAY_PATHS = ['/', '/company'];

export default function SiteHeader() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // 下へスクロールすると隠れ、上へ戻すと現れる
  useEffect(() => {
    let lastY = window.scrollY;
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 50);
      if (Math.abs(y - lastY) > 8) {
        setHidden(y > 240 && y > lastY);
        lastY = y;
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const overlay = OVERLAY_PATHS.includes(pathname) && !scrolled && !menuOpen;

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-50 transition-[transform,background-color,box-shadow] duration-700 ease-out-expo ${
        hidden && !menuOpen ? '-translate-y-full' : 'translate-y-0'
      } ${overlay ? 'bg-transparent' : 'bg-white/95 backdrop-blur-sm shadow-sm'}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 lg:py-8">
        <div className="flex justify-between items-center">
          <Link
            to="/"
            className={`text-xl lg:text-2xl font-light tracking-[0.2em] transition-colors duration-500 ${
              overlay ? 'text-white' : 'text-stone-800'
            }`}
          >
            SPIN MOVE
          </Link>
          <div className="flex items-center gap-4 lg:gap-16">
            <div className="hidden lg:flex items-center gap-12">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `nav-link text-sm font-light tracking-wider whitespace-nowrap transition-colors duration-300 ${
                      isActive
                        ? `is-active ${overlay ? 'text-amber-200' : 'text-amber-700'}`
                        : overlay
                          ? 'text-white hover:text-amber-200'
                          : 'text-stone-700 hover:text-amber-700'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
            <Link
              to="/contact"
              className="btn-sweep bg-amber-700 text-white px-5 py-2.5 lg:px-8 lg:py-3 text-xs lg:text-sm font-light tracking-wider whitespace-nowrap"
            >
              お問い合わせ
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'メニューを閉じる' : 'メニューを開く'}
              aria-expanded={menuOpen}
              className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors duration-500 ${
                overlay ? 'text-white' : 'text-stone-800'
              }`}
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden grid transition-[grid-template-rows] duration-500 ease-out-expo ${
          menuOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden" inert={!menuOpen}>
          <div className={`mobile-menu bg-white border-t border-stone-200 px-6 py-6 space-y-4 ${menuOpen ? 'is-open' : ''}`}>
            {NAV_ITEMS.map((item, i) => (
              <NavLink
                key={item.to}
                to={item.to}
                style={{ '--i': i } as CSSProperties}
                className={({ isActive }) =>
                  `block text-sm font-light tracking-wider py-2 transition-colors ${
                    isActive ? 'text-amber-700' : 'text-stone-700 hover:text-amber-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}
